import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  LuPlay,
  LuPause,
  LuFileText,
  LuChevronDown,
  LuChevronUp,
  LuCheck,
} from "react-icons/lu"; 
import SongSimpliLogo from "./SongSimpliLogo";
import ThemeToggle from "./ThemeToggle";
import facebookIcon from "../assets/icons/facebook.svg";
import LuDownload from "../assets/icons/downloadIcon.svg";
import chatIcon from "../assets/icons/chatIcon.svg";
import copyIcon from "../assets/icons/copyIcon.svg";
import shareIcon from "../assets/icons/moreIcon.svg";
import type { GenerateMusicResult } from "../utils/generateMusic";
import { saveMusicExportFlow } from "../utils/musicExport";
import { triggerDownloadBlob } from "../hooks/useAudioDownload";
import { ROUTE_CONSTANT } from "../routing/route-constants";
import { getLocalizedPath } from "../utils/helper";
import { buildDefaultSongBackgroundUrl } from "../utils/general-config";

import { extractSongDisplayDetails, getSongCardBackgroundImage } from "../utils/musicDisplay";
import { getDisplayLyricLines } from "../utils/lyricsSanitize";

interface MusicPlayerScreenProps {
  musicResult?: GenerateMusicResult | null;
  isDark?: boolean;
}

function formatTime(secs: number): string {
  if (isNaN(secs) || secs < 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function MusicPlayerScreen({
  musicResult,
  isDark: initialIsDark,
}: MusicPlayerScreenProps) {
  const navigate = useNavigate();
  const { locale } = useParams<{ locale?: string }>();
  const localeVal = locale ?? "";
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isDark, setIsDark] = useState(
    () => initialIsDark ?? localStorage.getItem("theme") !== "light"
  );

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
  }, [isDark]);

  const toggleDark = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLyricsOpen, setIsLyricsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    recipientName,
    occasion,
    genre,
    songTitle,
    songDescription,
    fileExt,
  } = extractSongDisplayDetails(musicResult);

  const [bgImageUrl, setBgImageUrl] = useState<string>(() =>
    getSongCardBackgroundImage({ occasion, genre })
  );

  useEffect(() => {
    const candidate = getSongCardBackgroundImage({ occasion, genre });
    const fallback = buildDefaultSongBackgroundUrl();

    if (candidate === fallback) {
      setBgImageUrl(fallback);
      return;
    }

    const img = new Image();
    img.src = candidate;
    img.onload = () => {
      setBgImageUrl(candidate);
    };
    img.onerror = () => {
      setBgImageUrl(fallback);
    };
  }, [occasion, genre]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      if (audio.duration && !isNaN(audio.duration) && Number.isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    if (audio.readyState >= HTMLMediaElement.HAVE_METADATA && audio.duration && !isNaN(audio.duration)) {
      setDuration(audio.duration);
    }

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("durationchange", handleLoadedMetadata);
    audio.addEventListener("canplay", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("durationchange", handleLoadedMetadata);
      audio.removeEventListener("canplay", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [musicResult?.audioBlobUrl]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch((err) => console.warn("Audio play error:", err));
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seekTime = parseFloat(e.target.value);
    setCurrentTime(seekTime);
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
    }
  };

  const handleCreateNewSong = () => {
    navigate(
      `${getLocalizedPath(localeVal, {
        Path: ROUTE_CONSTANT.MUSIC_EDITOR,
        LocalePath: ROUTE_CONSTANT.MUSIC_EDITOR_LOCALE,
      })}?onboarding=true`,
    );
  };

  const handleCopyLink = () => {
    const currentUrl = window.location.href;
    navigator.clipboard.writeText(currentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleShareFacebook = () => {
    const shareUrl = encodeURIComponent(window.location.href);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      "_blank",
      "width=600,height=400"
    );
  };

  const handleShareText = () => {
    const shareUrl = window.location.href;
    const msg = encodeURIComponent(`Listen to this personalized song made for ${recipientName}: ${shareUrl}`);
    window.open(`sms:?body=${msg}`, "_blank");
  };

  const handleShareMore = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: songTitle,
          text: `Listen to this personalized song for ${recipientName}!`,
          url: window.location.href,
        });
      } catch (err) {
        console.warn("Share cancelled or failed:", err);
      }
    } else {
      handleCopyLink();
    }
  };

  const handleDownloadSong = async () => {
    if (isDownloading || !musicResult) return;
    setIsDownloading(true);
    const ext = fileExt.toLowerCase();
    const downloadFilename = `${songTitle || "Custom_Song"}.${ext}`;
    try {
      if (musicResult.audioBlob && musicResult.audioBlob.size > 0) {
        triggerDownloadBlob(musicResult.audioBlob, downloadFilename);
      } else {
        await saveMusicExportFlow({
          music: musicResult,
          convertedFormat: ext,
        });
      }
    } catch (err) {
      console.error(`Failed to download ${fileExt}:`, err);
      if (musicResult.audioBlobUrl) {
        const a = document.createElement("a");
        a.href = musicResult.audioBlobUrl;
        a.download = downloadFilename;
        a.click();
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const displayLyricLines = getDisplayLyricLines(musicResult?.lyricsSections);

  const isAudioReady = Boolean(
    musicResult?.audioBlobUrl || (musicResult?.audioBlob && musicResult.audioBlob.size > 0)
  );

  const recipientPossessiveName =
    recipientName.endsWith("'s") || recipientName.endsWith("’s")
      ? recipientName
      : recipientName.endsWith("s")
        ? `${recipientName}’`
        : `${recipientName}'s`;

  const subtitleFullText = `Here’s the one-of-a-kind ${genre} song for ${recipientPossessiveName} ${occasion}`;

  return (
    <div
      className="relative flex h-screen w-full flex-col overflow-hidden"
      style={{
        background: isDark
          ? "radial-gradient(ellipse 80% 65% at 50% 109%, #FF00D633 0%, rgba(255, 0, 214, 0.2) 50%, transparent 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #eee3ff 64%, #b3e2f0 100%)",
      }}
    >
      {/* Nav (Pinned at top) */}
      <header
        className={`shrink-0 z-20 flex flex-col sm:flex-row sm:items-center sm:justify-between sm:py-5 p-[10px] sm:px-5 gap-3 ${
          isDark ? "" : "border-b border-[#EAEBED] sm:border-b-0"
        }`}
      >
        <div className="flex items-center justify-between sm:justify-start gap-[4.29px]">
          <SongSimpliLogo isDark={isDark} alt="PDFSimpli Logo" />
          <div className="sm:hidden">
            <ThemeToggle isDark={isDark} onToggle={toggleDark} />
          </div>
        </div>

        <div className="flex-1" />

        <div className="hidden sm:block">
          <ThemeToggle isDark={isDark} onToggle={toggleDark} />
        </div>
      </header>

      {/* Main Content (Scrollable) */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col items-center py-6 sm:py-10 pb-24 sm:pb-32 px-4 sm:px-6 w-full">
        {musicResult?.audioBlobUrl && (
          <audio
            ref={audioRef}
            src={musicResult.audioBlobUrl}
            preload="metadata"
          />
        )}

        <div className="w-full flex flex-col items-center gap-3 sm:gap-6">
          <div className="flex flex-col items-center text-center gap-1 sm:gap-2 w-full">
          <h1 className="text-[18px] sm:text-[44px] font-semibold text-[#16283D] dark:text-[#fff] leading-relaxed">
            Your song is ready!
          </h1>
          <div className="group relative w-full flex justify-center">
            <p
              className={`text-[12px] sm:text-[24px] px-8 sm:px-2  max-w-[1100px] font-normal leading-4 sm:leading-relaxed line-clamp-3 ${
                isDark ? "text-[#94a3b8]" : "text-[#16283D]"
              }`}
            >
              Here’s the one-of-a-kind <span className="lowercase">{genre}</span>{" "}
              song for <span className=" ">{recipientPossessiveName}</span>{" "}
              <span className="lowercase">{occasion}</span>
            </p>
            <span
              className="pointer-events-none absolute top-full left-1/2 z-50 mt-2 -translate-x-1/2 whitespace-normal break-words rounded-lg bg-white text-[#16283D] dark:bg-[#1F2937] dark:text-white px-3 py-1.5 text-left text-[12px] font-normal opacity-0 shadow-xl ring-1 ring-black/10 dark:ring-white/10 transition-opacity duration-150 group-hover:opacity-100"
              style={{ maxWidth: "min(90vw, 480px)" }}
            >
              {subtitleFullText}
              <span className="absolute left-1/2 bottom-full h-2 w-2 -translate-x-1/2 translate-y-1 rotate-45 bg-white dark:bg-[#1F2937]" />
            </span>
          </div>

          <button
            type="button"
            id="createNewSong"
            onClick={handleCreateNewSong}
            className="w-full sm:w-[300px] mt-3 min-h-[47px] sm:min-h-[62px] inline-flex items-center justify-center cursor-pointer rounded-full bg-[#0e76ff] hover:bg-[#0b63d4] text-white px-8 sm:px-12 h-[52px] sm:h-[62px] text-[14px] sm:text-[18px] font-bold uppercase tracking-wider transition-all duration-200 shadow-md hover:shadow-lg active:scale-95"
          >
            Create new song
          </button>
        </div>
        <div className="w-full max-w-[620px] flex flex-col items-center gap-6"> 
          <div className="relative w-full rounded-[10px] overflow-hidden shadow-2xl bg-neutral-900 border border-neutral-700 aspect-4/3 sm:aspect-video flex flex-col justify-between p-5 sm:p-7 group">
            <div
              className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
              style={{
                backgroundImage: `url('${bgImageUrl}')`,
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/65" />

            {/* Top: Occasion + Genre Pill, Title, Subtitle */}
            <div className="relative z-10 flex flex-col items-start gap-1 text-white max-w-[95%]">
              <div className="flex items-center gap-2.5 sm:gap-3 max-w-full min-w-0">
                <span className="group relative min-w-0 max-w-full">
                  <span className="block text-white text-[12px] sm:text-[20px] font-bold capitalize drop-shadow truncate">
                   {occasion}
                  </span>
                  <span
                    className="pointer-events-none absolute top-full left-0 z-50 mt-2 whitespace-normal break-words rounded-lg bg-white text-[#16283D] dark:bg-[#1F2937] dark:text-white px-3 py-1.5 text-left text-[12px] font-normal opacity-0 shadow-xl ring-1 ring-black/10 dark:ring-white/10 transition-opacity duration-150 group-hover:opacity-100"
                    style={{ maxWidth: "min(90vw, 320px)" }}
                  >
                   {occasion}
                    <span className="absolute left-3 bottom-full h-2 w-2 translate-y-1 rotate-45 bg-white dark:bg-[#1F2937]" />
                  </span>
                </span>
                <span className="bg-white text-neutral-900 rounded-full px-3 sm:px-[15px] py-1 sm:py-[8px] h-[28px] sm:h-[35px] inline-flex items-center justify-center text-[10px] sm:text-[16px] font-bold capitalize shadow-sm leading-none">
                  {genre}
                </span>
              </div>

              <h2 className="text-[16px] sm:text-[34px] font-normal sm:font-semibold tracking-tight drop-shadow-md line-clamp-1 leading-relaxed sm:leading-12">
                {songTitle}
              </h2>
              <p className="text-[10px] sm:text-[18px] text-white font-normal drop-shadow leading-relaxed line-clamp-2">
                {songDescription}
              </p>
            </div>

            {/* Center: Play/Pause button */}
            <div className="relative z-10 flex flex-col items-center justify-center my-auto py-2">
              <button
                type="button"
                id="playerPlayPause"
                onClick={togglePlayPause}
                disabled={!isAudioReady}
                className="size-12 sm:size-16 rounded-full bg-[#0e76ff] hover:bg-[#0b63d4] hover:scale-110 active:scale-95 text-white flex items-center justify-center shadow-xl transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                aria-label={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? (
                  <LuPause className="size-5 sm:size-7" fill="currentColor" />
                ) : (
                  <LuPlay className="size-5 sm:size-7 ml-0.5" fill="currentColor" />
                )}
              </button>
            </div>

            {/* Bottom: Progress Bar & Timestamps */}
            <div className="relative z-10 flex flex-col gap-4 pt-2">
              <div className="relative w-full flex items-center">
                <input
                  type="range"
                  min={0}
                  max={duration || 100}
                  step={0.1}
                  value={currentTime}
                  disabled={!isAudioReady}
                  onChange={handleSeek}
                  className="w-full h-1.5 bg-white/40 rounded-lg appearance-none cursor-pointer accent-[#0e76ff] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(to right, #0e76ff ${progressPercent}%, rgba(255,255,255,0.4) ${progressPercent}%)`,
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs sm:text-[13px] text-white font-medium drop-shadow">
                <span>{formatTime(currentTime)}</span>
                <span>
                  {formatTime(
                    duration ||
                      (musicResult?.timestamps
                        ? musicResult.timestamps[musicResult.timestamps.length - 1]?.endMs / 1000
                        : 94)
                  )}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`w-full rounded-2xl border px-4 py-5 sm:p-5 flex flex-col gap-3 shadow-sm ${
              isDark
                ? "bg-[#1e293b] border-[#334155]"
                : "bg-white border-[#D2E5FF]"
            }`}
          >
            <h3
              className={`text-[16px] sm:text-[20px] font-bold ${
                isDark ? "text-white" : "text-[#16283D]"
              }`}
            >
              Share your song
            </h3>
            <div className="flex flex-nowrap items-center gap-[3px] sm:gap-2.5">
              {/* Facebook */}
              <button
                type="button"
                id="shareFacebook"
                onClick={handleShareFacebook}
                disabled={!isAudioReady}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 sm:py-1.5 sm:pl-[5px] w-full max-w-[24%] sm:min-w-[107px] p-2 sm:pr-3 rounded-[7px] border text-[12px]  sm:text-[16px] font-semibold transition-all hover:bg-opacity-80 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer w-auto shrink-0 ${
                  isDark
                    ? "border-[#0e76ff] bg-[#0f172a] text-white hover:bg-[#334155]"
                    : "border-[#D2E5FF] bg-[#f8fafc] text-[#334155] hover:bg-[#f1f5f9]"
                }`}
              >
                <div className="size-6 sm:size-9 rounded-[6px] flex items-center justify-center shrink-0">
                  <img src={facebookIcon} alt="Facebook" className="size-6 sm:size-9 object-contain" />
                </div>
                <span>Facebook</span>
              </button>

              {/* Text / Chat */}
              <button
                type="button"
                id="shareText"
                onClick={handleShareText}
                disabled={!isAudioReady}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 sm:py-1.5 sm:pl-[5px] w-full max-w-[24%] sm:min-w-[107px] p-2 sm:pr-3 rounded-[7px] border text-[12px]  sm:text-[16px] font-semibold transition-all hover:bg-opacity-80 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer w-auto shrink-0 ${
                  isDark
                    ? "border-[#0e76ff] bg-[#0f172a] text-white hover:bg-[#334155]"
                    : "border-[#D2E5FF] bg-[#f8fafc] text-[#16283D] hover:bg-[#f1f5f9]"
                }`}
              >
                <div className="size-6 sm:size-9 rounded-[6px] flex items-center justify-center bg-[#E8F7E7] dark:bg-[#143820] shrink-0">
                  <img src={chatIcon} alt="Text" className="size-6 sm:size-9 object-contain" />
                </div>
                <span>Text</span>
              </button>

              {/* Copy */}
              <button
                type="button"
                id="shareCopy"
                onClick={handleCopyLink}
                disabled={!isAudioReady}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 sm:py-1.5 sm:pl-[5px] w-full max-w-[24%] sm:min-w-[107px] p-2 sm:pr-3 rounded-[7px] border text-[12px]  sm:text-[16px] font-semibold transition-all hover:bg-opacity-80 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer w-auto shrink-0 ${
                  isDark
                    ? "border-[#0e76ff] bg-[#0f172a] text-white hover:bg-[#334155]"
                    : "border-[#D2E5FF] bg-[#f8fafc] text-[#16283D] hover:bg-[#f1f5f9]"
                }`}
              >
                <div className="size-6 sm:size-9 rounded-[6px] flex items-center justify-center bg-[#D2EAFF] dark:bg-[#173a5e] shrink-0">
                  {copied ? (
                    <LuCheck className="size-4 text-[#0e76ff]" />
                  ) : (
                    <img src={copyIcon} alt="Copy" className="size-6 sm:size-9 object-contain" />
                  )}
                </div>
                <span>{copied ? "Copied!" : "Copy"}</span>
              </button>

              {/* More / Share */}
              <button
                type="button"
                id="shareMore"
                onClick={handleShareMore}
                disabled={!isAudioReady}
                className={`flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 sm:py-1.5 sm:pl-[5px] w-full max-w-[24%] sm:min-w-[107px] p-2 sm:pr-3 rounded-[7px] border text-[12px]  sm:text-[16px] font-semibold transition-all hover:bg-opacity-80 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer w-auto shrink-0 ${
                  isDark
                    ? "border-[#0e76ff] bg-[#0f172a] text-white hover:bg-[#334155]"
                    : "border-[#D2E5FF] bg-[#f8fafc] text-[#16283D] hover:bg-[#f1f5f9]"
                }`}
              >
                <div className="size-6 sm:size-9 rounded-[6px] flex items-center justify-center bg-[#F5EDFF] dark:bg-[#341b52] shrink-0">
                  <img src={shareIcon} alt="More" className="size-6 sm:size-9 object-contain" />
                </div>
                <span>More</span>
              </button>
            </div>
          </div>

          <div
            className={`w-full rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm ${
              isDark
                ? "bg-[#1e293b] border-[#334155]"
                : "bg-white border-[#e2e8f0]"
            }`}
          >
            <div className="flex items-center gap-3.5">
              <div className="size-[45px] rounded-[7px] dark:bg-[#581c87]/30 flex items-center justify-center shrink-0">
                <img src={LuDownload} className="size-[45px] text-[#9747FF] dark:text-[#fff]" />
              </div>
              <div className="flex flex-col">
                <span
                  className={`text-[16px] sm:text-[20px] font-semibold leading-tight ${
                    isDark ? "text-white" : "text-[#1e293b]"
                  }`}
                >
                  {fileExt} download
                </span>
                <span
                  className={`text-[12px] sm:text-[16px] font-normal mt-0.5 ${
                    isDark ? "text-[#94a3b8]" : "text-[#64748b]"
                  }`}
                >
                  Download high-quality {fileExt}
                </span>
              </div>
            </div>

            <button
              type="button"
              id="downloadSong"
              onClick={handleDownloadSong}
              disabled={!isAudioReady || isDownloading}
              className="w-full sm:w-auto inline-flex items-center justify-center px-8 sm:px-10 h-[52px] sm:h-[62px] rounded-full text-white font-normal sm:font-bold text-[16px] sm:text-[18px] tracking-wider uppercase transition-all duration-200 shadow-md hover:shadow-lg active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-center"
              style={{
                background:
                  "linear-gradient(90deg, #f95a38 0%, #fc792e 50%, #ff9823 100%)",
              }}
            >
              {isDownloading ? "Downloading…" : `Download song`}
            </button>
          </div>

          <div
            className={`w-full rounded-2xl border overflow-hidden transition-all shadow-sm ${
              isDark
                ? "bg-[#1e293b] border-[#334155]"
                : "bg-white border-[#e2e8f0]"
            }`}
          >
            <button
              type="button"
              id="songLyricsToggle"
              onClick={() => setIsLyricsOpen(!isLyricsOpen)}
              className="w-full py-4 px-5 flex items-center justify-center gap-2 cursor-pointer select-none"
            >
              <LuFileText className="size-5 sm:size-6 text-[#0e76ff]" />
              <span
                className={`text-[16px] sm:text-[20px] font-semibold ${
                  isDark ? "text-white" : "text-[#1e293b]"
                }`}
              >
                Song lyrics
              </span>
              {isLyricsOpen ? (
                <LuChevronUp className="size-4 sm:size-5 text-[#64748b]" />
              ) : (
                <LuChevronDown className="size-4 sm:size-5 text-[#64748b]" />
              )}
            </button>

            {isLyricsOpen && (
              <div
                id="songLyricsContent"
                className={`px-6 pb-6 pt-2 border-t text-center text-[15px] sm:text-[18px] font-normal leading-relaxed ${
                  isDark
                    ? "border-[#334155] text-[#cbd5e1]"
                    : "border-[#f1f5f9] text-[#475569]"
                }`}
              >
                {displayLyricLines.length > 0 ? (
                  <div className="flex flex-col gap-1 sm:gap-2">
                    {displayLyricLines.map((line, idx) => (
                      <p key={idx} className="text-[#16283D] dark:text-[#fff] font-normal leading-normal">
                        {line}
                      </p>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 flex flex-col items-center justify-center text-center">
                    <p className={`text-sm font-normal ${isDark ? "text-[#94a3b8]" : "text-[#64748b]"}`}>
                      Lyrics are not available for this song.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  </div>
);
}
